import React from "react";
import { useNavigate } from "react-router-dom";
import { X, Check, Eye, RotateCcw, ClipboardCheck, ArrowRight, Loader2, RefreshCw, AlertTriangle, Bell, BellOff, SlidersHorizontal } from "lucide-react";
import { api } from "../lib/api";
import { pushSupported, getPushStatus, enablePush, disablePush, getPushPrefs, savePushPrefs } from "../lib/push";

const SEV = {
  high: { badge: "bg-red-100 text-red-700", label: "Prioritaire", bar: "bg-red-500" },
  medium: { badge: "bg-amber-100 text-amber-800", label: "À faire", bar: "bg-amber-500" },
  low: { badge: "bg-slate-100 text-slate-600", label: "Info", bar: "bg-slate-400" },
};

export default function TaskCenter({ open, onClose, dark = false, onCounts }) {
  const [data, setData] = React.useState({ todo: [], done: [] });
  const [tab, setTab] = React.useState("todo");
  const [loading, setLoading] = React.useState(true);
  const [updateAvailable, setUpdateAvailable] = React.useState(typeof window !== "undefined" && !!window.__junagoUpdateReady);
  const [confirmUpd, setConfirmUpd] = React.useState(false);
  const navigate = useNavigate();

  React.useEffect(() => {
    const on = () => setUpdateAvailable(true);
    window.addEventListener("junago-update-available", on);
    return () => window.removeEventListener("junago-update-available", on);
  }, []);
  const applyUpdate = () => { if (typeof window !== "undefined" && window.__junagoApplyUpdate) window.__junagoApplyUpdate(); };

  const [push, setPush] = React.useState({ supported: false, enabled: false });
  const [pushBusy, setPushBusy] = React.useState(false);
  React.useEffect(() => {
    if (!open || !pushSupported()) return;
    getPushStatus().then(setPush);
  }, [open]);
  const togglePush = async () => {
    if (pushBusy) return;
    setPushBusy(true);
    try {
      if (push.enabled) { await disablePush(); setPush((p) => ({ ...p, enabled: false })); }
      else { await enablePush(); setPush((p) => ({ ...p, enabled: true, permission: "granted" })); try { await api.post("/push/test"); } catch { /* noop */ } }
    } catch (e) { /* refus/non supporté : on ignore */ }
    finally { setPushBusy(false); }
  };

  const [prefsOpen, setPrefsOpen] = React.useState(false);
  const [prefs, setPrefs] = React.useState(null);
  React.useEffect(() => {
    if (!open || !prefsOpen || prefs) return;
    getPushPrefs().then(setPrefs).catch(() => setPrefs({ new_booking: true, departures: true, returns: true }));
  }, [open, prefsOpen, prefs]);
  const togglePref = async (key) => {
    const next = { ...(prefs || {}), [key]: !(prefs || {})[key] };
    setPrefs(next);
    try { await savePushPrefs(next); } catch { /* noop */ }
  };
  const PREF_ITEMS = [
    { key: "new_booking", label: "Nouvelles réservations", hint: "Interne et boutique en ligne" },
    { key: "departures", label: "Départs du jour", hint: "Récapitulatif à 08h00" },
    { key: "returns", label: "Retours du jour", hint: "Récapitulatif à 08h00" },
  ];

  const load = React.useCallback(() => {
    api.get("/vendor/taskcenter")
      .then(({ data }) => {
        const todo = data.todo || [];
        setData({ todo, done: data.done || [] });
        onCounts?.({ todo: todo.length, unseen: todo.filter((t) => !t.seen).length });
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [onCounts]);

  React.useEffect(() => {
    load();
    window.addEventListener("taskcenter-changed", load);
    window.addEventListener("copilot-tasks-changed", load);
    return () => {
      window.removeEventListener("taskcenter-changed", load);
      window.removeEventListener("copilot-tasks-changed", load);
    };
  }, [load]);

  React.useEffect(() => {
    if (!open) return;
    load();
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const markSeen = async (t) => {
    setData((d) => {
      const todo = d.todo.map((x) => (x.id === t.id ? { ...x, seen: true } : x));
      onCounts?.({ todo: todo.length, unseen: todo.filter((x) => !x.seen).length });
      return { ...d, todo };
    });
    try { await api.post(`/vendor/taskcenter/${t.id}/seen`); } catch { /* noop */ }
  };

  const markDone = async (t) => {
    setData((d) => {
      const todo = d.todo.filter((x) => x.id !== t.id);
      onCounts?.({ todo: todo.length, unseen: todo.filter((x) => !x.seen).length });
      return { todo, done: [{ ...t, done_at: new Date().toISOString() }, ...d.done] };
    });
    try { await api.post(`/vendor/taskcenter/${t.id}/done`); } catch { load(); }
  };

  const reopen = async (t) => {
    try { await api.post(`/vendor/taskcenter/${t.id}/reopen`); } catch { /* noop */ }
    load();
  };

  const goAction = (t) => {
    if (!t.action_url) return;
    onClose();
    navigate(t.action_url);
  };

  if (!open) return null;
  const list = tab === "todo" ? data.todo : data.done;

  return (
    <div
      className={`fixed top-16 right-3 bottom-3 z-50 w-[min(460px,calc(100vw-24px))] flex flex-col rounded-2xl border shadow-2xl overflow-hidden ${
        dark ? "bg-[#12121F] border-white/10 shadow-black/50" : "bg-white border-slate-200 shadow-slate-400/25"
      }`}
      style={{ animation: "jnTaskIn .22s cubic-bezier(.2,.8,.3,1) both" }}
      data-testid="taskcenter-panel"
    >
      <style>{`@keyframes jnTaskIn{from{opacity:0;transform:translateX(24px)}to{opacity:1;transform:none}}`}</style>
      {/* Header */}
      <div className={`shrink-0 px-5 pt-4 pb-3 border-b ${dark ? "border-white/10" : "border-slate-100"}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <div className={`h-8 w-8 rounded-xl flex items-center justify-center ${dark ? "bg-white/10 text-violet-300" : "bg-violet-100 text-violet-700"}`}>
              <ClipboardCheck className="h-4 w-4" />
            </div>
            <div>
              <div className={`text-sm font-bold ${dark ? "text-white" : "text-slate-900"}`}>Gestionnaire de tâches</div>
              <div className={`text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>Généré automatiquement par Junago</div>
            </div>
          </div>
          <div className="flex items-center gap-1">
            {pushSupported() && (
              <button
                onClick={() => setPrefsOpen((v) => !v)}
                title="Réglages des notifications"
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                  prefsOpen
                    ? "text-violet-600 bg-violet-50"
                    : dark ? "text-slate-400 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"
                }`}
                data-testid="taskcenter-notif-settings"
                aria-label="Réglages des notifications"
                aria-expanded={prefsOpen}
              >
                <SlidersHorizontal className="h-4 w-4" />
              </button>
            )}
            {pushSupported() && (
              <button
                onClick={togglePush}
                disabled={pushBusy}
                title={push.enabled ? "Désactiver les notifications téléphone" : "Activer les notifications téléphone"}
                className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors disabled:opacity-50 ${
                  push.enabled
                    ? "text-violet-600 bg-violet-50"
                    : dark ? "text-slate-400 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"
                }`}
                data-testid="taskcenter-push-toggle"
                aria-label="Notifications téléphone"
              >
                {pushBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : push.enabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
              </button>
            )}
            <button
              onClick={onClose}
              className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${dark ? "text-slate-400 hover:bg-white/10" : "text-slate-500 hover:bg-slate-100"}`}
              data-testid="taskcenter-close"
              aria-label="Fermer"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
        {prefsOpen && pushSupported() && (
          <div
            className={`mt-3 rounded-xl border p-3 ${dark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"}`}
            data-testid="taskcenter-notif-settings-panel"
          >
            <div className={`text-[11px] font-semibold mb-2 ${dark ? "text-slate-300" : "text-slate-600"}`}>
              M'alerter pour
            </div>
            <div className="space-y-1.5">
              {PREF_ITEMS.map((it) => {
                const on = prefs ? !!prefs[it.key] : true;
                return (
                  <button
                    key={it.key}
                    onClick={() => togglePref(it.key)}
                    disabled={!prefs}
                    className={`w-full flex items-center justify-between gap-3 rounded-lg px-2.5 py-2 text-left transition-colors disabled:opacity-60 ${
                      dark ? "hover:bg-white/5" : "hover:bg-white"
                    }`}
                    data-testid={`taskcenter-pref-${it.key}`}
                    role="switch"
                    aria-checked={on}
                  >
                    <span className="min-w-0">
                      <span className={`block text-xs font-medium ${dark ? "text-white" : "text-slate-800"}`}>{it.label}</span>
                      <span className={`block text-[10px] ${dark ? "text-slate-400" : "text-slate-500"}`}>{it.hint}</span>
                    </span>
                    <span
                      className={`shrink-0 h-5 w-9 rounded-full p-0.5 transition-colors ${on ? "bg-violet-600" : dark ? "bg-white/15" : "bg-slate-300"}`}
                    >
                      <span className={`block h-4 w-4 rounded-full bg-white transition-transform ${on ? "translate-x-4" : ""}`} />
                    </span>
                  </button>
                );
              })}
            </div>
            <div className={`mt-2 text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>
              {push.enabled ? "Appliqué aux notifications de cet appareil." : "Activez les notifications (cloche) pour les recevoir sur ce téléphone."}
            </div>
          </div>
        )}
        {/* Tabs */}
        <div className={`mt-3 flex items-center gap-1 rounded-full p-0.5 border w-fit ${dark ? "border-white/10 bg-white/5" : "border-slate-200 bg-slate-50"}`}>
          <button
            onClick={() => setTab("todo")}
            className={`h-7 px-3.5 rounded-full text-[11px] font-semibold transition-colors ${tab === "todo" ? (dark ? "bg-white/15 text-white" : "bg-slate-900 text-white") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`}
            data-testid="taskcenter-tab-todo"
          >
            À faire {data.todo.length > 0 && <span className="ml-1 opacity-70">({data.todo.length})</span>}
          </button>
          <button
            onClick={() => setTab("done")}
            className={`h-7 px-3.5 rounded-full text-[11px] font-semibold transition-colors ${tab === "done" ? (dark ? "bg-white/15 text-white" : "bg-slate-900 text-white") : (dark ? "text-slate-400 hover:text-slate-200" : "text-slate-500 hover:text-slate-800")}`}
            data-testid="taskcenter-tab-done"
          >
            Terminées {data.done.length > 0 && <span className="ml-1 opacity-70">({data.done.length})</span>}
          </button>
        </div>
      </div>

      {/* List */}
      <div className="flex-1 min-h-0 overflow-y-auto p-3 space-y-2" data-testid="taskcenter-list">
        {updateAvailable && (
          <div className="relative rounded-xl border border-emerald-200 bg-emerald-50 p-3 pl-4" data-testid="taskcenter-update-entry">
            <span className="absolute left-0 top-3 bottom-3 w-[3px] rounded-full bg-emerald-500" />
            <div className="flex items-start gap-2.5">
              <div className="h-7 w-7 rounded-lg bg-emerald-100 text-emerald-700 flex items-center justify-center shrink-0">
                <RefreshCw className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs font-bold text-emerald-900">Nouvelle version disponible</div>
                <div className="mt-0.5 text-[11px] leading-snug text-emerald-700">Profitez des dernières améliorations de Junago. Une nouvelle version est prête à être appliquée.</div>
                {confirmUpd ? (
                  <div className="mt-2">
                    <div className="flex items-start gap-1.5 text-[11px] text-amber-700 mb-2">
                      <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0" />
                      <span>Enregistrez votre travail en cours (réservation, devis, formulaire) avant d'actualiser.</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={applyUpdate} className="h-8 px-3 rounded-lg bg-emerald-600 text-white text-[11px] font-bold hover:bg-emerald-500 transition-colors" data-testid="taskcenter-update-apply">Actualiser maintenant</button>
                      <button onClick={() => setConfirmUpd(false)} className="h-8 px-3 rounded-lg text-[11px] font-semibold text-slate-500 hover:bg-slate-100 transition-colors" data-testid="taskcenter-update-cancel">Plus tard</button>
                    </div>
                  </div>
                ) : (
                  <button onClick={() => setConfirmUpd(true)} className="mt-2 h-8 px-3.5 rounded-lg bg-emerald-600 text-white text-[11px] font-bold inline-flex items-center gap-1.5 hover:bg-emerald-500 transition-colors" data-testid="taskcenter-update-btn">
                    <RefreshCw className="h-3.5 w-3.5" /> Actualiser
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
        {loading ? (
          <div className="py-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-violet-500" /></div>
        ) : list.length === 0 ? (
          <div className="py-16 text-center">
            <ClipboardCheck className={`h-8 w-8 mx-auto mb-3 ${dark ? "text-slate-600" : "text-slate-300"}`} />
            <div className={`text-sm ${dark ? "text-slate-400" : "text-slate-500"}`}>
              {tab === "todo" ? "Tout est à jour, rien à faire 👌" : "Aucune tâche terminée pour l'instant."}
            </div>
          </div>
        ) : (
          list.map((t) => (
            <TaskRow
              key={t.id}
              task={t}
              done={tab === "done"}
              dark={dark}
              onSeen={() => markSeen(t)}
              onDone={() => markDone(t)}
              onReopen={() => reopen(t)}
              onAction={() => goAction(t)}
            />
          ))
        )}
      </div>
    </div>
  );
}

function TaskRow({ task: t, done, dark, onSeen, onDone, onReopen, onAction }) {
  const sev = SEV[t.severity] || SEV.low;
  return (
    <div
      className={`relative rounded-xl border p-3 pl-4 transition-colors ${
        dark ? "border-white/10 bg-white/[0.03] hover:bg-white/[0.06]" : done ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      data-testid={`taskcenter-task-${t.id}`}
    >
      <span className={`absolute left-0 top-3 bottom-3 w-[3px] rounded-full ${done ? "bg-emerald-400" : sev.bar}`} />
      <div className="flex items-start gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            {!done && !t.seen && <span className="h-1.5 w-1.5 rounded-full bg-violet-500 shrink-0" data-testid={`taskcenter-unseen-${t.id}`} />}
            <span className={`text-xs font-semibold truncate ${done ? "line-through " : ""}${dark ? "text-slate-100" : "text-slate-900"}`}>{t.title}</span>
          </div>
          {t.description && (
            <div className={`mt-0.5 text-[11px] leading-snug ${dark ? "text-slate-400" : "text-slate-500"}`}>{t.description}</div>
          )}
          <div className="mt-1.5 flex items-center gap-1.5 flex-wrap">
            {!done && <span className={`px-1.5 py-0.5 rounded-full text-[9px] font-bold ${sev.badge}`}>{sev.label}</span>}
            {t.date && <span className={`text-[10px] ${dark ? "text-slate-500" : "text-slate-400"}`}>{t.date}</span>}
            {done && t.done_at && (
              <span className="text-[10px] text-emerald-500 font-semibold" data-testid={`taskcenter-done-meta-${t.id}`}>
                Terminée le {new Date(t.done_at).toLocaleString("fr-FR", { dateStyle: "short", timeStyle: "short" })}
                {t.done_via === "auto" ? " · automatique" : " · manuelle"}
                {t.done_by ? ` · ${t.done_by}` : ""}
              </span>
            )}
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!done && t.action_url && (
            <button
              onClick={onAction}
              title={t.action_label || "Ouvrir"}
              className={`h-7 px-2.5 rounded-lg text-[10px] font-bold inline-flex items-center gap-1 transition-colors ${dark ? "bg-white/10 text-white hover:bg-white/20" : "bg-slate-900 text-white hover:bg-slate-800"}`}
              data-testid={`taskcenter-action-${t.id}`}
            >
              {t.action_label || "Ouvrir"} <ArrowRight className="h-3 w-3" />
            </button>
          )}
          {!done && !t.seen && (
            <button
              onClick={onSeen}
              title="Marquer comme vu"
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${dark ? "text-slate-400 hover:bg-white/10" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
              data-testid={`taskcenter-seen-${t.id}`}
            >
              <Eye className="h-3.5 w-3.5" />
            </button>
          )}
          {!done ? (
            <button
              onClick={onDone}
              title="Marquer comme terminée"
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${dark ? "text-emerald-400 hover:bg-emerald-500/15" : "text-emerald-600 hover:bg-emerald-50"}`}
              data-testid={`taskcenter-done-${t.id}`}
            >
              <Check className="h-4 w-4" strokeWidth={2.5} />
            </button>
          ) : (
            <button
              onClick={onReopen}
              title="Rouvrir la tâche"
              className={`h-7 w-7 rounded-lg flex items-center justify-center transition-colors ${dark ? "text-slate-400 hover:bg-white/10" : "text-slate-400 hover:bg-slate-100 hover:text-slate-700"}`}
              data-testid={`taskcenter-reopen-${t.id}`}
            >
              <RotateCcw className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
